import React, { useState, useEffect } from 'react'
import Card from '@material-ui/core/Card'
import Grid from '@material-ui/core/Grid'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import CardMedia from '@material-ui/core/CardMedia'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import LaunchIcon from '@material-ui/icons/Launch'
import { makeStyles } from '@material-ui/core/styles'
import articles from "./articles"

const useStyles = makeStyles(() => ({
  blogContainer: {
    padding: '2rem 0',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 30% 20%, rgba(120, 119, 198, 0.2) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(255, 119, 198, 0.2) 0%, transparent 50%)',
      pointerEvents: 'none',
      zIndex: 0
    }
  },
  gridContainer: {
    position: 'relative',
    zIndex: 1
  },
  cardWrapper: {
    maxWidth: 420,
    margin: '1rem auto',
    '&:hover $card': {
      transform: 'translateY(-12px) scale(1.02)',
      boxShadow: '0 25px 60px 0 rgba(31, 38, 135, 0.5)',
      background: 'rgba(255, 255, 255, 0.15)',
      borderColor: 'rgba(255, 255, 255, 0.4)',
      '&::before': {
        transform: 'scaleX(1)',
      }
    },
    '&:hover $cardImage': {
      transform: 'scale(1.08)',
    },
    '&:hover $imageOverlay': {
      opacity: 0.8,
    },
    '&:hover $externalIcon': {
      opacity: 1,
      transform: 'scale(1) rotate(0deg)',
    }
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
    borderRadius: '20px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #a8edea 100%)',
      transform: 'scaleX(0)',
      transformOrigin: 'left',
      transition: 'transform 0.4s ease',
      zIndex: 2
    }
  },
  partBadge: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 3,
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(15px)',
    WebkitBackdropFilter: 'blur(15px)',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.7rem',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
  },
  cardImage: {
    height: 240,
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden'
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3), rgba(118, 75, 162, 0.3))',
    opacity: 0.5,
    transition: 'opacity 0.3s ease'
  },
  externalIcon: {
    position: 'absolute',
    top: 16,
    right: 16,
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(15px)',
    WebkitBackdropFilter: 'blur(15px)',
    borderRadius: '50%',
    padding: 8,
    opacity: 0,
    transform: 'scale(0.8) rotate(-45deg)',
    transition: 'all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    '& svg': {
      color: 'white',
      fontSize: '1.2rem',
      filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
    }
  },
  cardContent: {
    flexGrow: 1,
    padding: '1.5rem',
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)'
  },
  cardTitle: {
    fontSize: '1.1rem',
    lineHeight: '1.4',
    fontWeight: '600',
    marginBottom: '1rem',
    color: 'rgba(255, 255, 255, 0.95)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
  },
  cardDescription: {
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: '1.5',
    fontSize: '0.9rem',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
  },
  cardActions: {
    padding: '1rem 1.5rem',
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  metaInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.8rem',
    fontWeight: '500',
    '& svg': {
      fontSize: '0.9rem',
      filter: 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2))'
    }
  },
  shineEffect: {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)',
    transition: 'transform 0.8s ease',
    pointerEvents: 'none',
    zIndex: 1
  }
}))

const trunc = (text, length) => {
  if (text.length < length) return text
  return text.substr(0, length) + "..."
}

const BlogRoll = () => {
  const classes = useStyles()
  const [visibleCards, setVisibleCards] = useState([])

  useEffect(() => {
    articles.forEach((_, index) => {
      setTimeout(() => {
        setVisibleCards(prev => [...prev, index])
      }, index * 200)
    })
  }, [])

  return (
    <Box className={classes.blogContainer}>
      <Grid container spacing={4} justify="center" alignItems="stretch" className={classes.gridContainer}>
        {articles.map(({ id, title, link, timeToRead, description, date, featuredimage }, index) => (
          <Grid 
            item 
            key={id} 
            xs={12} 
            sm={6} 
            md={6} 
            lg={4}
            className={classes.cardWrapper}
            style={{
              opacity: visibleCards.includes(index) ? 1 : 0,
              transform: visibleCards.includes(index) ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              transitionDelay: `${index * 0.15}s`
            }}
          >
            <Box
              component="a"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                textDecoration: "none", 
                color: "inherit",
                display: 'block',
                height: '100%'
              }}
              aria-label={`Read article: ${title}`}
            >
              <Card className={classes.card}>
                <CardMedia
                  className={classes.cardImage}
                  image={featuredimage}
                  title={title}
                >
                  <Box className={classes.imageOverlay} />
                </CardMedia>

                <CardContent className={classes.cardContent}>
                  <Typography
                    variant="h6"
                    component="h2"
                    className={classes.cardTitle}
                  >
                    {title}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    component="p"
                    className={classes.cardDescription}
                  >
                    {trunc(description, 120)}
                  </Typography>
                </CardContent>

                <CardActions className={classes.cardActions}>
                  <Box className={classes.metaInfo}>
                    <CalendarTodayIcon />
                    <span>{date}</span>
                  </Box>
                  
                  <Box className={classes.metaInfo}>
                    <AccessTimeIcon />
                    <span>{timeToRead} min read</span>
                  </Box>
                </CardActions>

                <Box className={classes.shineEffect} />
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default BlogRoll